import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import axios from "axios";
import apiRoutes from "../../assets/apiRoutes.json";
import ungroupedRoutesData from "../../const/RoutesList";
import homeShortcuts from "../../assets/logo-home-shortcuts.png";
import { getAppSettings } from "../../common/Utils";
import { parseShortcutsApiResponse, parseGlobalShortcutsFromUserProfile } from "../../common/shortcutsApi";
import { AppSettingsLocalStorageKey } from "../../const/constants";
import "./LandingQuickActions.css";

function getQuickActionsLayout() {
    const s = getAppSettings();
    return s && s.HomeQuickActionsLayout ==="classic"?"classic": "dashboard";
}

function getShowGlobalShortcuts() {
    const s = getAppSettings();
    return s == null || s.ShowGlobalShortcuts !== false;
}

/**
 * Tooltip caption: "Module -> route name" from RoutesList (e.g. Warehouse Management -> Codes -> Item Data Entry).
 * Falls back to shortcut label or path when the route is not in the list.
 */
function getQuickActionTooltipCaption(path, fallbackLabel) {
    if (!path) return fallbackLabel || null;
    const route = ungroupedRoutesData.find((r) => r.value === path);
    if (route) {
        const mod = route.key != null && String(route.key).trim();
        return mod ? `${route.key} -> ${route.name}` : route.name;
    }
    if (fallbackLabel) return fallbackLabel;
    return path;
}

function renderShortcutIcon(icon, variant) {
    const usePng = window._env_?.REACT_APP_MENU_USE_PNG;
    const wrapClass = `lqa-tile-iconWrap lqa-tile-iconWrap--${variant}`;

    if (usePng && (!icon || typeof icon !== "string" || !icon.startsWith("fa "))) {
        return (
            <span className={wrapClass}>
                <img src={homeShortcuts} alt="" className="lqa-tile-icon-img" />
            </span>
        );
    }
    if (typeof icon === "string" && icon.startsWith("fa ")) {
        return (
            <span className={wrapClass}>
                <i className={`lqa-tile-icon-fa ${icon}`} aria-hidden />
            </span>
        );
    }
    if (typeof icon === "string" && (icon.startsWith("/") || icon.startsWith("http") || icon.includes("."))) {
        return (
            <span className={wrapClass}>
                <img src={icon} alt="" className="lqa-tile-icon-img" />
            </span>
        );
    }
    return (
        <span className={wrapClass}>
            <i className="lqa-tile-icon-fa fa fa-external-link" aria-hidden />
        </span>
    );
}

function QuickActionButton({ item, onNavigate, variant, tooltipCaption }) {
    const label = item.text || item.name || item.title || "Open";
    const hasPath = Boolean(item.path);
    const caption = tooltipCaption && String(tooltipCaption).trim() ? String(tooltipCaption).trim() : null;
    const ariaDetail = caption ? `${label}. ${caption}` : label;

    return (
        <span
            className="lqa-tile-wrap"
            {...(caption ? { "data-lqa-caption": caption } : {})}
        >
            <button
                type="button"
                className={`lqa-tile lqa-tile--${variant}`}
                disabled={!hasPath}
                onClick={() => hasPath && onNavigate(item.path)}
                aria-label={hasPath ? `Open ${ariaDetail}` : ariaDetail}
            >
                {renderShortcutIcon(item.icon, variant)}
                <span className="lqa-tile-label">{label}</span>
            </button>
        </span>
    );
}

function DashboardSection({ sectionId, accent, badgeIcon, title, description, wide, shortcutBodyScroll, children }) {
    const hid = sectionId;
    return (
        <section
            className={`lqa-widget lqa-widget--accent-${accent}${wide ? " lqa-widget--wide" : ""}${
                shortcutBodyScroll ? " lqa-widget--shortcut-body-scroll" : ""
            }`}
            aria-labelledby={hid}
        >
            <header className="lqa-widget-head">
                <div className={`lqa-widget-head-icon lqa-widget-head-icon--${accent}`} aria-hidden>
                    <i className={badgeIcon} />
                </div>
                <div className="lqa-widget-head-text">
                    <div className="lqa-widget-eyebrow">Quick actions</div>
                    <h2 className="lqa-widget-title" id={hid}>
                        {title}
                    </h2>
                    <p className="lqa-widget-desc">{description}</p>
                </div>
            </header>
            <div className="lqa-widget-body">{children}</div>
        </section>
    );
}

/** Bolt + title row (no surrounding panel — each shortcut is its own card in the grid). */
function ClassicSectionHeading({ sectionId, title, first }) {
    return (
        <h2
            className={`lqa-classic-heading-title${first ? " lqa-classic-heading-title--first" : ""}`}
            id={sectionId}
        >
            <span className="lqa-classic-bolt" aria-hidden>
                <i className="fa fa-bolt" />
            </span>
            <span className="lqa-classic-heading-label">
                <strong>{title}</strong>
            </span>
        </h2>
    );
}

function EmptyState({ message, iconClass = "fa fa-info-circle", accent = "neutral" }) {
    return (
        <div className={`lqa-empty lqa-empty--${accent}`} role="status">
            <div className="lqa-empty-icon">
                <i className={iconClass} aria-hidden />
            </div>
            <p className="lqa-empty-text">{message}</p>
        </div>
    );
}

function PersonalShortcutsSkeleton() {
    return (
        <div className="lqa-loading" aria-hidden>
            {[1, 2, 3, 4, 5, 6].map((k) => (
                <div key={k} className="lqa-skeleton" />
            ))}
        </div>
    );
}

const LandingQuickActions = (props) => {
    const history = useHistory();
    const location = useLocation();
    const accessToken = props.oidc?.user?.access_token;
    const identityId = props.oidc?.user?.profile?.identityId;
    const [personalShortcuts, setPersonalShortcuts] = useState([]);
    const [globalShortcuts, setGlobalShortcuts] = useState([]);
    const [personalLoaded, setPersonalLoaded] = useState(false);
    const [mostUsedRefresh, setMostUsedRefresh] = useState(0);
    const [layout, setLayout] = useState(() => getQuickActionsLayout());
    const [showGlobalShortcuts, setShowGlobalShortcuts] = useState(() => getShowGlobalShortcuts());

    useEffect(() => {
        const refreshFromAppSettings = () => {
            setLayout(getQuickActionsLayout());
            setShowGlobalShortcuts(getShowGlobalShortcuts());
        };
        const onStorage = (e) => {
            if (!e.key || e.key === AppSettingsLocalStorageKey) refreshFromAppSettings();
        };
        window.addEventListener("storage", onStorage);
        window.addEventListener("argos-app-settings-changed", refreshFromAppSettings);
        return () => {
            window.removeEventListener("storage", onStorage);
            window.removeEventListener("argos-app-settings-changed", refreshFromAppSettings);
        };
    }, []);

    useEffect(() => {
        setLayout(getQuickActionsLayout());
        setShowGlobalShortcuts(getShowGlobalShortcuts());
    }, [location.pathname]);

    useEffect(() => {
        if (showGlobalShortcuts) {
            setGlobalShortcuts(parseGlobalShortcutsFromUserProfile(props.oidc?.user?.profile));
        } else {
            setGlobalShortcuts([]);
        }
    }, [showGlobalShortcuts, props.oidc?.user?.profile]);

    useEffect(() => {
        if (!accessToken) {
            setPersonalLoaded(true);
            return;
        }
        let cancelled = false;
        axios
            .get(`${window._env_.REACT_APP_APIS}/${apiRoutes.Shortcuts.GetShortcuts}`, {
                params: { includeGlobal: false },
                headers: {
                    "Content-Type": "application/json",
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                },
            })
            .then((res) => {
                if (!cancelled && res.data != null) {
                    const { personal } = parseShortcutsApiResponse(res.data);
                    setPersonalShortcuts(personal);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setPersonalShortcuts([]);
                }
            })
            .finally(() => {
                if (!cancelled) setPersonalLoaded(true);
            });
        return () => {
            cancelled = true;
        };
    }, [accessToken]);

    useEffect(() => {
        const bump = () => setMostUsedRefresh((n) => n + 1);
        window.addEventListener("focus", bump);
        document.addEventListener("visibilitychange", bump);
        return () => {
            window.removeEventListener("focus", bump);
            document.removeEventListener("visibilitychange", bump);
        };
    }, []);

    const mostUsedItems = useMemo(() => {
        if (!identityId) return [];
        const key = `${identityId}-visitedPages`;
        const visited = JSON.parse(localStorage.getItem(key)) || [];
        return visited.map((path) => {
            const matchingRoute = ungroupedRoutesData.find((route) => route.value === path);
            const text = matchingRoute ? matchingRoute.name.split("->").pop().trim() : path;
            return { path, text, icon: "fa fa-history" };
        });
    }, [identityId, location.pathname, mostUsedRefresh]);

    const navigate = (path) => {
        if (path) history.push(path);
    };

    const personalWithPath = personalShortcuts.filter((s) => s && s.path);
    const globalWithPath = globalShortcuts.filter((s) => s && s.path);

    const globalBodyDashboard = !personalLoaded ? (
        <PersonalShortcutsSkeleton />
    ) : globalWithPath.length === 0 ? (
        <EmptyState
            accent="neutral"
            iconClass="fa fa-globe"
            message="No organization-wide shortcuts are available yet. Your administrator can configure shared shortcuts for all users."
        />
    ) : (
        <div className="lqa-grid lqa-grid--global">
            {globalWithPath.map((item) => (
                <QuickActionButton
                    key={item.id || `g-${item.path}-${item.text}`}
                    tooltipCaption={getQuickActionTooltipCaption(item.path, item.text)}
                    item={item}
                    onNavigate={navigate}
                    variant="global"
                />
            ))}
        </div>
    );

    const globalBodyClassic = !personalLoaded ? (
        <p className="lqa-classic-hint">Loading shortcuts…</p>
    ) : globalWithPath.length === 0 ? (
        <p className="lqa-classic-hint">No organization-wide shortcuts yet. Shared shortcuts are managed by an administrator.</p>
    ) : (
            <div className="lqa-grid lqa-grid--classic">
                {globalWithPath.map((item) => (
                    <QuickActionButton
                        key={item.id || `g-${item.path}-${item.text}`}
                        tooltipCaption={getQuickActionTooltipCaption(item.path, item.text)}
                        item={item}
                        onNavigate={navigate}
                        variant="global"
                    />
                ))}
            </div>
    );

    const personalBodyDashboard = !personalLoaded ? (
        <PersonalShortcutsSkeleton />
    ) : personalWithPath.length === 0 ? (
        <EmptyState
            accent="personal"
            iconClass="fa fa-star-o"
            message='No shortcuts yet. Right-click a tree item and choose "Add to Shortcuts".'
        />
    ) : (
        <div className="lqa-grid lqa-grid--compact">
            {personalWithPath.map((item) => (
                <QuickActionButton
                    key={item.id || `${item.path}-${item.text}`}
                    tooltipCaption={getQuickActionTooltipCaption(item.path, item.text)}
                    item={item}
                    onNavigate={navigate}
                    variant="personal"
                />
            ))}
        </div>
    );

    const personalBodyClassic = !personalLoaded ? (
        <p className="lqa-classic-hint">Loading shortcuts…</p>
    ) : personalWithPath.length === 0 ? (
        <p className="lqa-classic-hint">
            No personal shortcuts yet. Right-click a page in the sidebar and choose &quot;Add to Shortcuts&quot;
            (same list as the Shortcuts folder in the menu).
        </p>
    ) : (
        <div className="lqa-grid lqa-grid--classic">
            {personalWithPath.map((item) => (
                <QuickActionButton
                    key={item.id || `${item.path}-${item.text}`}
                    tooltipCaption={getQuickActionTooltipCaption(item.path, item.text)}
                    item={item}
                    onNavigate={navigate}
                    variant="personal"
                />
            ))}
        </div>
    );

    const mostUsedBodyDashboard =
        mostUsedItems.length === 0 ? (
            <EmptyState
                accent="history"
                iconClass="fa fa-compass"
                message="Open modules from the menu to populate this list automatically."
            />
        ) : (
            <div className="lqa-grid lqa-grid--compact">
                {mostUsedItems.map((item) => (
                    <QuickActionButton
                        key={item.path}
                        tooltipCaption={getQuickActionTooltipCaption(item.path, item.text)}
                        item={item}
                        onNavigate={navigate}
                        variant="history"
                    />
                ))}
            </div>
        );

    const mostUsedBodyClassic =
        mostUsedItems.length === 0 ? (
            <p className="lqa-classic-hint">
                Pages you visit (except Home) are tracked automatically and appear here.
            </p>
        ) : (
            <div className="lqa-grid lqa-grid--classic">
                {mostUsedItems.map((item) => (
                    <QuickActionButton
                        key={item.path}
                        tooltipCaption={getQuickActionTooltipCaption(item.path, item.text)}
                        item={item}
                        onNavigate={navigate}
                        variant="history"
                    />
                ))}
            </div>
        );

    return (
        <div className={`lqa${layout === "classic" ? " lqa--classic-shell" : ""}`}>
            {layout === "classic" ? (
            <div className="lqa-classic-shortcut-body-scroll">
                <div className="lqa-classic">
                    {showGlobalShortcuts ? (
                        <section className="lqa-classic-group" aria-labelledby="lqa-global-shortcuts">
                            <ClassicSectionHeading sectionId="lqa-global-shortcuts" title="Global Shortcuts" first />
                            {globalBodyClassic}
                        </section>
                    ) : null}
                    <section className="lqa-classic-group" aria-labelledby="lqa-personal-shortcuts">
                        <ClassicSectionHeading
                            sectionId="lqa-personal-shortcuts"
                            title="Personal Shortcuts"
                            first={!showGlobalShortcuts}
                        />
                        {personalBodyClassic}
                    </section>
                    <section className="lqa-classic-group" aria-labelledby="lqa-most-used-pages">
                        <ClassicSectionHeading sectionId="lqa-most-used-pages" title="Most Used Pages" />
                        {mostUsedBodyClassic}
                    </section>
                </div>
                </div>
            ) : (
                <div className="lqa-dashboard">
                    <div className="lqa-canvas">
                        <header className="lqa-dash-top">
                            <div className="lqa-dash-top-brand">
                                <span className="lqa-dash-top-logo" aria-hidden>
                                    <i className="fa fa-th-large" />
                                </span>
                                <div>
                                    <h2 className="lqa-dash-top-title">Workspace dashboard</h2>
                                    <p className="lqa-dash-top-sub">Shortcuts and recent destinations in one place.</p>
                                </div>
                            </div>
                        </header>

                        <div className="lqa-bento">
                            {showGlobalShortcuts ? (
                                <DashboardSection
                                    sectionId="lqa-global-shortcuts"
                                    accent="global"
                                    badgeIcon="fa fa-globe"
                                    title="Global Shortcuts"
                                    description="Role-specific entry points from your administrator."
                                    wide
                                    shortcutBodyScroll
                                >
                                    {globalBodyDashboard}
                                </DashboardSection>
                            ) : null}

                            <DashboardSection
                                sectionId="lqa-personal-shortcuts"
                                accent="personal"
                                badgeIcon="fa fa-bookmark"
                                title="Personal Shortcuts"
                                description="Saved from the sidebar — same as your Shortcuts folder."
                            >
                                {personalBodyDashboard}
                            </DashboardSection>

                            <DashboardSection
                                sectionId="lqa-most-used-pages"
                                accent="history"
                                badgeIcon="fa fa-signal"
                                title="Most Used Pages"
                                description="Recent routes, updated as you navigate."
                            >
                                {mostUsedBodyDashboard}
                            </DashboardSection>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

function mapStateToProps(state) {
    return { oidc: state.oidc };
}

export default connect(mapStateToProps)(LandingQuickActions);
